import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, BrainCircuit, CheckCircle, Cpu, Image as ImageIcon, Key, Loader2, MapPin, Newspaper, Play, RefreshCw, Save, ShieldCheck, Thermometer, Truck, Zap } from 'lucide-react';

type AiModelOption = {
  id: string;
  displayName: string;
  inputTokenLimit: number | null;
  outputTokenLimit: number | null;
};

type KeyStatus = {
  hasYouTubeKey: boolean;
  source: 'firestore' | 'env' | 'none';
  hasSvvKey?: boolean;
  svvSource?: 'firestore' | 'env' | 'none';
  hasTrafikverketKey?: boolean;
  trafikverketSource?: 'firestore' | 'env' | 'none';
};

type AdminAiConfigPanelProps = {
  aiPrompt: string;
  onAiPromptChange: (value: string) => void;
  onResetAiConfig: () => void;
  onSaveAiConfig: () => void;
  processingId: string | null;
  isAiSaved: boolean;
  aiModel: string;
  availableModels: AiModelOption[];
  onChangeAiModel: (modelId: string) => void;
  isModelSaved: boolean;
  articlePrompt: string;
  onArticlePromptChange: (value: string) => void;
  onSaveArticlePrompt: () => void;
  isArticlePromptSaved: boolean;
  youtubeKeyStatus: KeyStatus | null;
  onSaveYouTubeKey: (key: string) => Promise<void>;
  isYouTubeKeySaved: boolean;
  imageModel: string;
  onChangeImageModel: (modelId: string) => void;
  isImageModelSaved: boolean;
  onSaveSvvKey: (key: string) => Promise<void>;
  isSvvKeySaved: boolean;
  onSaveMapillaryToken: (key: string) => Promise<void>;
  isMapillaryTokenSaved: boolean;
  onSaveYrKey: (key: string) => Promise<void>;
  isYrKeySaved: boolean;
  onSaveTrafikverketKey: (key: string) => Promise<void>;
  isTrafikverketKeySaved: boolean;
  onTestImageApis: () => Promise<any>;
  onBackfillImages: (batchSize?: number) => Promise<any>;
  onGetAutoImageStats: () => Promise<any>;
};

const DEFAULT_ARTICLE_PROMPT_PLACEHOLDER = `Du er en erfaren friluftsjournalist og bobilentusiast som skriver for Fricamp...

Skrivestil:
- Ærlig, norsk og hjelpsom
- Fra bobil-entusiast til bobil-entusiast

Oppgave: Skriv en engasjerende artikkel om fricamping for {{season}} ({{month}}).

Svar BARE med gyldig JSON:
{
  "title": "Fengende tittel",
  "summary": "2-3 setninger",
  "content": "<h2>...</h2><p>...</p>"
}

(Standard-prompt brukes automatisk om feltet er tomt)`;

const IMAGE_MODEL_OPTIONS = [
  { id: 'imagen-3.0-generate-002', label: 'Imagen 3 (Standard)' },
  { id: 'imagen-3.0-fast-generate-001', label: 'Imagen 3 (Fast)' },
  { id: 'imagen-4.0-generate-preview-05-20', label: 'Imagen 4 (Preview)' },
];

const formatTokenLimit = (limit: number | null): string => {
  if (!limit) return '?';
  if (limit >= 1_000_000) return `${(limit / 1_000_000).toFixed(1)}M`;
  if (limit >= 1_000) return `${Math.round(limit / 1_000)}K`;
  return String(limit);
};

const AdminAiConfigPanel: React.FC<AdminAiConfigPanelProps> = ({
  aiPrompt,
  onAiPromptChange,
  onResetAiConfig,
  onSaveAiConfig,
  processingId,
  isAiSaved,
  aiModel,
  availableModels,
  onChangeAiModel,
  isModelSaved,
  articlePrompt,
  onArticlePromptChange,
  onSaveArticlePrompt,
  isArticlePromptSaved,
  youtubeKeyStatus,
  onSaveYouTubeKey,
  isYouTubeKeySaved,
  imageModel,
  onChangeImageModel,
  isImageModelSaved,
  onSaveSvvKey,
  isSvvKeySaved,
  onSaveMapillaryToken,
  isMapillaryTokenSaved,
  onSaveYrKey,
  isYrKeySaved,
  onSaveTrafikverketKey,
  isTrafikverketKeySaved,
  onTestImageApis,
  onBackfillImages,
  onGetAutoImageStats
}) => {
  const { t } = useTranslation();
  const selectedModel = availableModels.find(m => m.id === aiModel);
  const [ytKeyInput, setYtKeyInput] = React.useState('');
  const [ytKeySaving, setYtKeySaving] = React.useState(false);
  const [svvKeyInput, setSvvKeyInput] = React.useState('');
  const [svvKeySaving, setSvvKeySaving] = React.useState(false);
  const [mapillaryTokenInput, setMapillaryTokenInput] = React.useState('');
  const [mapillaryTokenSaving, setMapillaryTokenSaving] = React.useState(false);
  const [yrKeyInput, setYrKeyInput] = React.useState('');
  const [yrKeySaving, setYrKeySaving] = React.useState(false);
  const [trafikverketKeyInput, setTrafikverketKeyInput] = React.useState('');
  const [trafikverketKeySaving, setTrafikverketKeySaving] = React.useState(false);
  const [testResult, setTestResult] = React.useState<any>(null);
  const [isTesting, setIsTesting] = React.useState(false);
  const [backfillResult, setBackfillResult] = React.useState<any>(null);
  const [isBackfilling, setIsBackfilling] = React.useState(false);
  const [autoImageStats, setAutoImageStats] = React.useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = React.useState(false);

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4">
      {/* AI Model Selector */}
      <div className="bg-violet-500/5 border border-violet-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-violet-400">
          <Cpu size={24} />
          <div className="flex-1">
            <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.ai_model_title', 'AI-modell')}</h3>
            <p className="text-2xs font-bold opacity-70">{t('admin.ai_model_subtitle', 'Velg hvilken Gemini-modell som brukes for alle AI-operasjoner')}</p>
          </div>
          {isModelSaved && (
            <span className="flex items-center gap-1 text-emerald-400 text-2xs font-black uppercase tracking-widest animate-in fade-in">
              <CheckCircle size={14} /> Lagret
            </span>
          )}
          {processingId === 'ai-model-save' && (
            <Loader2 size={16} className="animate-spin text-violet-400" />
          )}
        </div>

        {availableModels.length > 0 ? (
          <div className="space-y-3">
            <select
              value={aiModel}
              onChange={(e) => onChangeAiModel(e.target.value)}
              disabled={processingId === 'ai-model-save'}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-violet-500 appearance-none cursor-pointer"
            >
              {availableModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.id})
                </option>
              ))}
            </select>
            {selectedModel && (
              <div className="flex gap-4 text-2xs text-[var(--text-muted)] font-mono">
                <span>Inn: {formatTokenLimit(selectedModel.inputTokenLimit)} tokens</span>
                <span>Ut: {formatTokenLimit(selectedModel.outputTokenLimit)} tokens</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[var(--text-muted)] text-xs py-2">
            <Loader2 size={14} className="animate-spin" />
            <span>{t('admin.ai_models_loading', 'Henter tilgjengelige modeller...')}</span>
          </div>
        )}
      </div>

      {/* Image Model Selector */}
      <div className="bg-indigo-500/5 border border-indigo-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-indigo-400">
          <ImageIcon size={24} />
          <div className="flex-1">
            <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.image_model_title', 'Bildemodell')}</h3>
            <p className="text-2xs font-bold opacity-70">{t('admin.image_model_subtitle', 'Velg hvilken Imagen-modell som brukes for AI-illustrasjoner')}</p>
          </div>
          {isImageModelSaved && (
            <span className="flex items-center gap-1 text-emerald-400 text-2xs font-black uppercase tracking-widest animate-in fade-in">
              <CheckCircle size={14} /> Lagret
            </span>
          )}
          {processingId === 'image-model-save' && (
            <Loader2 size={16} className="animate-spin text-indigo-400" />
          )}
        </div>

        <select
          value={imageModel}
          onChange={(e) => onChangeImageModel(e.target.value)}
          disabled={processingId === 'image-model-save'}
          className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500 appearance-none cursor-pointer"
        >
          {IMAGE_MODEL_OPTIONS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label} ({m.id})
            </option>
          ))}
        </select>
      </div>

      {/* AI Prompt Editor */}
      <div className="bg-orange-500/5 border border-orange-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-orange-500">
            <BrainCircuit size={24} />
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.ai_instructions_title', 'AI Instruksjoner')}</h3>
              <p className="text-2xs font-bold opacity-70">{t('admin.ai_instructions_subtitle', 'Rediger prompt for analyse av plasser')}</p>
            </div>
          </div>
          <button
            onClick={onResetAiConfig}
            className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-2"
          >
            <RefreshCw size={12} /> {t('common.reset', 'Tilbakestill')}
          </button>
        </div>

        <div className="space-y-2">
          <p className="text-[var(--text-secondary)] text-2xs leading-relaxed">
            {t('admin.ai_variables_help', 'Bruk {{variabel}} for å sette inn data. Tilgjengelige variabler: nearestHouse, highway, latitude, longitude, features.')}
          </p>
          <textarea
            value={aiPrompt}
            onChange={(e) => onAiPromptChange(e.target.value)}
            className="w-full h-96 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl p-4 text-xs font-mono text-[var(--text-secondary)] focus:outline-none focus:border-orange-500 resize-none leading-relaxed"
          />
        </div>

        <button
          onClick={onSaveAiConfig}
          disabled={processingId === 'ai-save'}
          className={`w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 ${isAiSaved ? 'bg-emerald-600 text-white' : 'bg-orange-600 hover:bg-orange-500 text-white'}`}
        >
          {processingId === 'ai-save' ? <Loader2 size={16} className="animate-spin" /> : isAiSaved ? <CheckCircle size={16} /> : <Save size={16} />}
          {isAiSaved ? t('common.saved', 'Lagret!') : t('admin.save_changes', 'Lagre endringer')}
        </button>
      </div>

      {/* Article AI Prompt Editor */}
      <div className="bg-teal-500/5 border border-teal-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-teal-400">
          <Newspaper size={24} />
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.article_prompt_title', 'Artikkel-prompt')}</h3>
            <p className="text-2xs font-bold opacity-70">{t('admin.article_prompt_subtitle', 'Prompt for AI-genererte artikler (kjører 1. og 15. hver måned)')}</p>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[var(--text-secondary)] text-2xs leading-relaxed">
            {t('admin.article_prompt_help', 'Bruk {{season}} og {{month}} som variabler. AI-en skal returnere JSON med title, summary og content (HTML). La feltet stå tomt for å bruke standard-prompten.')}
          </p>
          <textarea
            value={articlePrompt}
            onChange={(e) => onArticlePromptChange(e.target.value)}
            placeholder={DEFAULT_ARTICLE_PROMPT_PLACEHOLDER}
            className="w-full h-80 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl p-4 text-xs font-mono text-[var(--text-secondary)] focus:outline-none focus:border-teal-500 resize-none leading-relaxed placeholder:text-[var(--text-muted)]"
          />
        </div>

        <button
          onClick={onSaveArticlePrompt}
          disabled={processingId === 'article-prompt-save'}
          className={`w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 ${isArticlePromptSaved ? 'bg-emerald-600 text-white' : 'bg-teal-600 hover:bg-teal-500 text-white'}`}
        >
          {processingId === 'article-prompt-save' ? <Loader2 size={16} className="animate-spin" /> : isArticlePromptSaved ? <CheckCircle size={16} /> : <Save size={16} />}
          {isArticlePromptSaved ? t('common.saved', 'Lagret!') : t('admin.save_changes', 'Lagre endringer')}
        </button>
      </div>

      {/* YouTube API Key (secure) */}
      <div className="bg-rose-500/5 border border-rose-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-rose-400">
          <Key size={24} />
          <div className="flex-1">
            <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.youtube_key_title', 'YouTube API-nøkkel')}</h3>
            <p className="text-2xs font-bold opacity-70">{t('admin.youtube_key_subtitle', 'Brukes for å hente videoer fra YouTube Data API v3')}</p>
          </div>
          {isYouTubeKeySaved && (
            <span className="flex items-center gap-1 text-emerald-400 text-2xs font-black uppercase tracking-widest animate-in fade-in">
              <CheckCircle size={14} /> Lagret
            </span>
          )}
        </div>

        {/* Status indicator */}
        {youtubeKeyStatus && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-2xs font-bold ${
            youtubeKeyStatus.hasYouTubeKey
              ? 'bg-emerald-500/10 text-emerald-400'
              : 'bg-amber-500/10 text-amber-400'
          }`}>
            <ShieldCheck size={14} />
            {youtubeKeyStatus.hasYouTubeKey
              ? t('admin.youtube_key_configured', `Nøkkel konfigurert (${youtubeKeyStatus.source === 'firestore' ? 'lagret i admin' : 'miljøvariabel'})`)
              : t('admin.youtube_key_missing', 'Ingen nøkkel konfigurert — YouTube-søk er deaktivert')
            }
          </div>
        )}

        <div className="space-y-2">
          <p className="text-[var(--text-muted)] text-3xs leading-relaxed">
            {t('admin.youtube_key_help', 'Nøkkelen lagres sikkert server-side og er aldri synlig i appen. Du trenger en Google Cloud API-nøkkel med YouTube Data API v3 aktivert.')}
          </p>
          <input
            type="password"
            value={ytKeyInput}
            onChange={(e) => setYtKeyInput(e.target.value)}
            placeholder={youtubeKeyStatus?.hasYouTubeKey ? '••••••••••••••••••••' : t('admin.youtube_key_placeholder', 'Lim inn API-nøkkel her...')}
            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-tiny text-[var(--text-secondary)] font-mono focus:outline-none focus:border-rose-500 placeholder:text-[var(--text-muted)]"
          />
        </div>

        <button
          onClick={async () => {
            if (!ytKeyInput.trim()) return;
            setYtKeySaving(true);
            await onSaveYouTubeKey(ytKeyInput.trim());
            setYtKeyInput('');
            setYtKeySaving(false);
          }}
          disabled={ytKeySaving || !ytKeyInput.trim()}
          className="w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 bg-rose-600/80 hover:bg-rose-600 text-white disabled:opacity-40"
        >
          {ytKeySaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {t('admin.youtube_key_save', 'Lagre nøkkel sikkert')}
        </button>
      </div>

      {/* SVV API Key (Statens Vegvesen) */}
      <div className="bg-orange-500/5 border border-orange-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-orange-400">
          <Truck size={24} />
          <div className="flex-1">
            <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.svv_key_title', 'Statens Vegvesen API-nøkkel')}</h3>
            <p className="text-2xs font-bold opacity-70">{t('admin.svv_key_subtitle', 'Brukes av Garasje-funksjonen for kjøretøyoppslag')}</p>
          </div>
          {isSvvKeySaved && (
            <span className="flex items-center gap-1 text-emerald-400 text-2xs font-black uppercase tracking-widest animate-in fade-in">
              <CheckCircle size={14} /> Lagret
            </span>
          )}
        </div>

        {/* Status indicator */}
        {youtubeKeyStatus && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-2xs font-bold ${
            youtubeKeyStatus.hasSvvKey
              ? 'bg-emerald-500/10 text-emerald-400'
              : 'bg-amber-500/10 text-amber-400'
          }`}>
            <ShieldCheck size={14} />
            {youtubeKeyStatus.hasSvvKey
              ? t('admin.svv_key_configured', `Nøkkel konfigurert (${youtubeKeyStatus.svvSource === 'firestore' ? 'lagret i admin' : 'miljøvariabel'})`)
              : t('admin.svv_key_missing', 'Ingen nøkkel konfigurert — reg.nr-oppslag i Garasje er deaktivert')
            }
          </div>
        )}

        <div className="space-y-2">
          <p className="text-[var(--text-muted)] text-3xs leading-relaxed">
            {t('admin.svv_key_help', 'Nøkkelen lagres sikkert server-side. Du trenger en API-nøkkel fra Statens Vegvesen for kjøretøyopplysninger.')}
          </p>
          <input
            type="password"
            value={svvKeyInput}
            onChange={(e) => setSvvKeyInput(e.target.value)}
            placeholder={youtubeKeyStatus?.hasSvvKey ? '••••••••••••••••••••' : t('admin.svv_key_placeholder', 'Lim inn SVV API-nøkkel her...')}
            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-tiny text-[var(--text-secondary)] font-mono focus:outline-none focus:border-orange-500 placeholder:text-[var(--text-muted)]"
          />
        </div>

        <button
          onClick={async () => {
            if (!svvKeyInput.trim()) return;
            setSvvKeySaving(true);
            await onSaveSvvKey(svvKeyInput.trim());
            setSvvKeyInput('');
            setSvvKeySaving(false);
          }}
          disabled={svvKeySaving || !svvKeyInput.trim()}
          className="w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 bg-orange-600/80 hover:bg-orange-600 text-white disabled:opacity-40"
        >
          {svvKeySaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {t('admin.svv_key_save', 'Lagre nøkkel sikkert')}
        </button>
      </div>

      {/* Mapillary Access Token (street-level images) */}
      <div className="bg-green-500/5 border border-green-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-green-400">
          <MapPin size={24} />
          <div className="flex-1">
            <h3 className="text-sm font-black uppercase tracking-widest">Mapillary Access Token</h3>
            <p className="text-2xs font-bold opacity-70">Brukes for gatebilder og panorama nær campingplasser</p>
          </div>
          {isMapillaryTokenSaved && (
            <span className="flex items-center gap-1 text-emerald-400 text-2xs font-black uppercase tracking-widest animate-in fade-in">
              <CheckCircle size={14} /> Lagret
            </span>
          )}
        </div>

        {youtubeKeyStatus && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-2xs font-bold ${
            (youtubeKeyStatus as any).hasMapillaryToken
              ? 'bg-emerald-500/10 text-emerald-400'
              : 'bg-amber-500/10 text-amber-400'
          }`}>
            <ShieldCheck size={14} />
            {(youtubeKeyStatus as any).hasMapillaryToken
              ? `Token konfigurert (${(youtubeKeyStatus as any).mapillarySource === 'firestore' ? 'lagret i admin' : 'miljøvariabel'})`
              : 'Ingen token konfigurert — Mapillary-bilder er deaktivert'
            }
          </div>
        )}

        <div className="space-y-2">
          <p className="text-[var(--text-muted)] text-3xs leading-relaxed">
            Opprett en gratis Mapillary-app på mapillary.com/developer for å få et access token. Brukes for gatebilder nær plasser.
          </p>
          <input
            type="password"
            value={mapillaryTokenInput}
            onChange={(e) => setMapillaryTokenInput(e.target.value)}
            placeholder={(youtubeKeyStatus as any)?.hasMapillaryToken ? '••••••••••••••••••••' : 'Lim inn Mapillary access token her...'}
            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-tiny text-[var(--text-secondary)] font-mono focus:outline-none focus:border-green-500 placeholder:text-[var(--text-muted)]"
          />
        </div>

        <button
          onClick={async () => {
            if (!mapillaryTokenInput.trim()) return;
            setMapillaryTokenSaving(true);
            await onSaveMapillaryToken(mapillaryTokenInput.trim());
            setMapillaryTokenInput('');
            setMapillaryTokenSaving(false);
          }}
          disabled={mapillaryTokenSaving || !mapillaryTokenInput.trim()}
          className="w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 bg-green-600/80 hover:bg-green-600 text-white disabled:opacity-40"
        >
          {mapillaryTokenSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Lagre token sikkert
        </button>
      </div>

      {/* Yr Badetemperatur API Key */}
      <div className="bg-cyan-500/5 border border-cyan-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-cyan-400">
          <Thermometer size={24} />
          <div className="flex-1">
            <h3 className="text-sm font-black uppercase tracking-widest">Yr Badetemperatur API-nøkkel</h3>
            <p className="text-2xs font-bold opacity-70">Brukes for å vise vanntemperatur på badeplasser i Norge</p>
          </div>
          {isYrKeySaved && (
            <span className="flex items-center gap-1 text-emerald-400 text-2xs font-black uppercase tracking-widest animate-in fade-in">
              <CheckCircle size={14} /> Lagret
            </span>
          )}
        </div>

        {youtubeKeyStatus && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-2xs font-bold ${
            (youtubeKeyStatus as any).hasYrKey
              ? 'bg-emerald-500/10 text-emerald-400'
              : 'bg-amber-500/10 text-amber-400'
          }`}>
            <ShieldCheck size={14} />
            {(youtubeKeyStatus as any).hasYrKey
              ? `Nøkkel konfigurert (${(youtubeKeyStatus as any).yrSource === 'firestore' ? 'lagret i admin' : 'miljøvariabel'})`
              : 'Ingen nøkkel konfigurert — badetemperaturer fra Yr er deaktivert'
            }
          </div>
        )}

        <div className="space-y-2">
          <p className="text-[var(--text-muted)] text-3xs leading-relaxed">
            Send e-post til support@yr.no med emne «Forespørsel om API-nøkkel for badetemperaturer». Oppgi kontaktinfo og at du vil hente temperaturer. Kreditering: «Badetemperaturer levert av Yr».
          </p>
          <input
            type="password"
            value={yrKeyInput}
            onChange={(e) => setYrKeyInput(e.target.value)}
            placeholder={(youtubeKeyStatus as any)?.hasYrKey ? '••••••••••••••••••••' : 'Lim inn Yr API-nøkkel her...'}
            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-tiny text-[var(--text-secondary)] font-mono focus:outline-none focus:border-cyan-500 placeholder:text-[var(--text-muted)]"
          />
        </div>

        <button
          onClick={async () => {
            if (!yrKeyInput.trim()) return;
            setYrKeySaving(true);
            await onSaveYrKey(yrKeyInput.trim());
            setYrKeyInput('');
            setYrKeySaving(false);
          }}
          disabled={yrKeySaving || !yrKeyInput.trim()}
          className="w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 bg-cyan-600/80 hover:bg-cyan-600 text-white disabled:opacity-40"
        >
          {yrKeySaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Lagre nøkkel sikkert
        </button>
      </div>

      {/* Trafikverket API-nøkkel (Sverige) */}
      <div className="bg-sky-500/5 border border-sky-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-sky-400">
          <Truck size={24} />
          <div className="flex-1">
            <h3 className="text-sm font-black uppercase tracking-widest">Trafikverket API-nøkkel</h3>
            <p className="text-2xs font-bold opacity-70">Rastplatser og vägdata for Sverige</p>
          </div>
          {isTrafikverketKeySaved && (
            <div className="flex items-center gap-1 text-emerald-400 animate-pulse">
              <CheckCircle size={14} />
              <span className="text-3xs font-black uppercase">Lagret</span>
            </div>
          )}
        </div>

        {youtubeKeyStatus && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-2xs font-bold ${
            (youtubeKeyStatus as any).hasTrafikverketKey
              ? 'bg-emerald-500/10 text-emerald-400'
              : 'bg-amber-500/10 text-amber-400'
          }`}>
            <ShieldCheck size={14} />
            {(youtubeKeyStatus as any).hasTrafikverketKey
              ? `Nøkkel konfigurert (${(youtubeKeyStatus as any).trafikverketSource === 'firestore' ? 'lagret i admin' : 'miljøvariabel'})`
              : 'Ingen nøkkel konfigurert — svenske rastplatser fra Trafikverket er deaktivert'
            }
          </div>
        )}

        <div className="space-y-2">
          <p className="text-[var(--text-muted)] text-3xs leading-relaxed">
            Registrer gratis API-nøkkel på data.trafikverket.se. Brukes for å hente rastplatser og parkeringsplasser i Sverige under prescan og kartvisning.
          </p>
          <input
            type="password"
            value={trafikverketKeyInput}
            onChange={(e) => setTrafikverketKeyInput(e.target.value)}
            placeholder={(youtubeKeyStatus as any)?.hasTrafikverketKey ? '••••••••••••••••••••' : 'Lim inn Trafikverket API-nøkkel her...'}
            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-tiny text-[var(--text-secondary)] font-mono focus:outline-none focus:border-cyan-500 placeholder:text-[var(--text-muted)]"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={async () => {
              if (!trafikverketKeyInput.trim()) return;
              setTrafikverketKeySaving(true);
              await onSaveTrafikverketKey(trafikverketKeyInput.trim());
              setTrafikverketKeyInput('');
              setTrafikverketKeySaving(false);
            }}
            disabled={trafikverketKeySaving || !trafikverketKeyInput.trim()}
            className="flex-1 py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 bg-cyan-600/80 hover:bg-cyan-600 text-white disabled:opacity-40"
          >
            {trafikverketKeySaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Lagre nøkkel sikkert
          </button>
          {(youtubeKeyStatus as any)?.hasTrafikverketKey && (youtubeKeyStatus as any)?.trafikverketSource === 'firestore' && (
            <button
              onClick={async () => {
                if (!window.confirm('Slette lagret Trafikverket-nøkkel? Scanner i Sverige vil fungere uten rastplats-data til ny nøkkel lagres.')) return;
                setTrafikverketKeySaving(true);
                await onSaveTrafikverketKey('');
                setTrafikverketKeySaving(false);
              }}
              disabled={trafikverketKeySaving}
              className="px-4 py-4 rounded-xl text-2xs font-black uppercase tracking-widest bg-red-900/40 hover:bg-red-900/60 text-red-300 border border-red-700/50 disabled:opacity-40"
              title="Slett lagret nøkkel"
            >
              Slett
            </button>
          )}
        </div>
      </div>

      {/* Auto-bilder: Test, Backfill & Statistikk */}
      <div className="bg-violet-500/5 border border-violet-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-violet-400">
          <ImageIcon size={24} />
          <div className="flex-1">
            <h3 className="text-sm font-black uppercase tracking-widest">Auto-bilder</h3>
            <p className="text-2xs font-bold opacity-70">Test, backfill og statistikk for automatiske bilder</p>
          </div>
        </div>

        {/* Action buttons row */}
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={async () => {
              setIsTesting(true);
              setTestResult(null);
              try {
                const result = await onTestImageApis();
                setTestResult(result);
              } catch (e: any) {
                setTestResult({ error: e.message });
              } finally {
                setIsTesting(false);
              }
            }}
            disabled={isTesting}
            className="py-3 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all active:scale-95 bg-cyan-600/80 hover:bg-cyan-600 text-white disabled:opacity-40"
          >
            {isTesting ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {isTesting ? 'Tester...' : 'Test API'}
          </button>

          <button
            onClick={async () => {
              setIsBackfilling(true);
              setBackfillResult(null);
              try {
                const result = await onBackfillImages(20);
                setBackfillResult(result);
              } catch (e: any) {
                setBackfillResult({ error: e.message });
              } finally {
                setIsBackfilling(false);
              }
            }}
            disabled={isBackfilling}
            className="py-3 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all active:scale-95 bg-amber-600/80 hover:bg-amber-600 text-white disabled:opacity-40"
          >
            {isBackfilling ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {isBackfilling ? t('admin.ai.running') : 'Backfill'}
          </button>

          <button
            onClick={async () => {
              setIsLoadingStats(true);
              try {
                const result = await onGetAutoImageStats();
                setAutoImageStats(result);
              } catch (e: any) {
                setAutoImageStats({ error: e.message });
              } finally {
                setIsLoadingStats(false);
              }
            }}
            disabled={isLoadingStats}
            className="py-3 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all active:scale-95 bg-violet-600/80 hover:bg-violet-600 text-white disabled:opacity-40"
          >
            {isLoadingStats ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />}
            {isLoadingStats ? t('admin.ai.fetching') : 'Statistikk'}
          </button>
        </div>

        {/* Test results */}
        {testResult && (
          <div className="space-y-1.5">
            <span className="text-3xs font-bold text-cyan-400 uppercase tracking-widest">API-test</span>
            {testResult.error ? (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-2.5 text-red-400 text-2xs font-mono">
                Feil: {testResult.error}
              </div>
            ) : (
              testResult.results?.map((r: any, i: number) => (
                <div key={i} className={`rounded-lg p-2.5 text-2xs font-mono ${
                  r.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                }`}>
                  <span className="font-black">{r.source}:</span>{' '}
                  {r.success ? `Treff (${r.radius}m)${r.credit ? ` — ${r.credit}` : ''}` : (r.skipped ? t('admin.ai.missing_token') : t('admin.ai.no_results'))}
                </div>
              ))
            )}
          </div>
        )}

        {/* Backfill results */}
        {backfillResult && (
          <div className={`rounded-lg p-2.5 text-2xs font-mono ${
            backfillResult.error ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'
          }`}>
            <span className="font-black text-3xs uppercase tracking-widest text-amber-400">Backfill: </span>
            {backfillResult.error ? (
              <span>Feil: {backfillResult.error}</span>
            ) : (
              <span>{backfillResult.found}/{backfillResult.processed} funnet ({backfillResult.processed > 0 ? Math.round((backfillResult.found / backfillResult.processed) * 100) : 0}%) — {backfillResult.remaining} gjenstar</span>
            )}
          </div>
        )}

        {/* Statistics */}
        {autoImageStats && !autoImageStats.error && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-3xs font-bold text-violet-400 uppercase tracking-widest">Auto-bilder totalt</span>
              <span className="text-base font-black text-violet-400">{autoImageStats.totalWithAutoImage || 0}</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {autoImageStats.bySource && Object.entries(autoImageStats.bySource).map(([source, count]) => (
                <div key={source} className="flex items-center justify-between px-2 py-1 bg-[var(--bg-input)] rounded-lg">
                  <span className="text-3xs text-[var(--text-secondary)]">{source}</span>
                  <span className="text-2xs font-black text-violet-300">{count as number}</span>
                </div>
              ))}
              {autoImageStats.byAccuracy && Object.entries(autoImageStats.byAccuracy).map(([accuracy, count]) => (
                <div key={accuracy} className="flex items-center justify-between px-2 py-1 bg-[var(--bg-input)] rounded-lg">
                  <span className="text-3xs text-[var(--text-secondary)]">
                    {Number(accuracy) <= 100 ? t('admin.ai.accuracy_spot') : Number(accuracy) <= 500 ? t('admin.ai.accuracy_area') : t('admin.ai.accuracy_region')} ({accuracy}m)
                  </span>
                  <span className="text-2xs font-black text-violet-300">{count as number}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {autoImageStats?.error && (
          <div className="bg-red-500/10 rounded-lg p-2.5 text-red-400 text-2xs font-mono">Feil: {autoImageStats.error}</div>
        )}
      </div>
    </div>
  );
};

export default AdminAiConfigPanel;
